import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/auth";
import { LogOut, Globe2, Radar, Bookmark, Newspaper, GitBranch } from "lucide-react";
import Logo from "../components/Logo";

const FEATURES = [
  { icon: Globe2,    title: "1000s of live opportunities",  blurb: "UK, NATO, and EU defence opportunities refreshed daily." },
  { icon: Radar,     title: "Fit scoring",                   blurb: "Every notice scored against your CPV codes, certs and turnover." },
  { icon: Bookmark,  title: "Tracked tenders & checklists",  blurb: "Auto-generated bid-readiness checklist per opportunity." },
  { icon: Newspaper, title: "Live UK defence news ticker",   blurb: "UKDJ, Shephard, Defence Online, ADS — all in one feed." },
  { icon: GitBranch, title: "MOD pipeline & DASA",           blurb: "Future programmes & innovation funding from official sources." },
];

export default function Subscribe() {
  const { user, activeCompany, refresh, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [paymentStatus, setPaymentStatus] = useState(null);

  useEffect(() => {
    api.get("/billing/plans").then(({ data }) => {
      setPlan(data.find((p) => p.id === "standard") || data[0]);
    }).catch(() => {});
  }, []);

  // Auto-recovery: when a user lands on the paywall without a session_id in
  // the URL, silently ask the backend "is there an active Stripe subscription
  // for me that we just missed?". If yes — unlock and redirect. This catches
  // webhook misfires, closed Stripe tabs, and cross-browser logins.
  const [autoSynced, setAutoSynced] = useState(false);
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("session_id")) return;        // post-checkout flow handles itself
    if (autoSynced) return;
    if (!user) return;
    setAutoSynced(true);
    (async () => {
      try {
        const { data } = await api.post("/billing/sync");
        if (data?.activated) {
          const fresh = await refresh();
          if (fresh?.activeCompany?.subscription_active) {
            navigate("/app/dashboard", { replace: true });
          }
        }
      } catch (e) {
        // Auto-sync failed silently — user will still see paywall and can
        // click "Refresh my subscription" manually. Log for diagnostics.
        console.warn("subscribe auto-sync failed", e?.response?.status || e?.message);
      }
    })();
  }, [user, location.search, autoSynced, navigate, refresh]);

  // If user lands here with ?session_id=… we just came back from Stripe.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) return;
    let cancelled = false;
    let attempts = 0;
    setPaymentStatus("processing");

    const checkActive = async () => {
      try {
        const fresh = await refresh();
        if (fresh?.activeCompany?.subscription_active) {
          if (!cancelled) {
            setPaymentStatus("paid");
            setTimeout(() => navigate("/app/dashboard", { replace: true }), 600);
          }
          return true;
        }
      } catch (e) {
        console.warn("subscribe refresh error", e?.response?.status || e?.message);
      }
      return false;
    };

    const poll = async () => {
      if (cancelled) return;
      attempts++;
      // 1. /auth/me (which now auto-syncs from Stripe internally)
      if (await checkActive()) return;
      // 2. Direct Stripe checkout status lookup
      try {
        const { data } = await api.get(`/billing/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          await checkActive();
          return;
        }
        if (data.status === "expired") {
          if (!cancelled) setPaymentStatus("expired");
          return;
        }
      } catch (e) {
        const code = e?.response?.status;
        if (code === 401 || code === 403) {
          if (!cancelled) {
            setError(formatApiErrorDetail(e.response?.data?.detail) || "Session lookup unauthorised");
            setPaymentStatus("timeout");
          }
          return;
        }
        console.warn("subscribe status poll error", code || e?.message);
      }
      if (attempts >= 20) {
        if (!cancelled) setPaymentStatus("timeout");
        return;
      }
      if (!cancelled) setTimeout(poll, 2500);
    };

    // Most aggressive path first: backend /billing/sync (4-strategy Stripe lookup).
    (async () => {
      try { await api.post("/billing/sync"); } catch (_) { /* ignore, fall through */ }
      if (await checkActive()) return;
      poll();
    })();

    return () => { cancelled = true; };
  }, [location.search, navigate, refresh]);

  const startCheckout = async () => {
    setError(""); setLoading(true);
    try {
      const { data } = await api.post("/billing/checkout", {
        plan: "standard",
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      setLoading(false);
    }
  };

  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState("");
  const [showSessionPaste, setShowSessionPaste] = useState(false);
  const [sessionIdInput, setSessionIdInput] = useState("");
  const syncSubscription = async () => {
    setSyncing(true); setSyncMsg(""); setError("");
    try {
      const { data } = await api.post("/billing/sync");
      if (data?.activated) {
        setSyncMsg("Found your subscription — unlocking your workspace…");
        const fresh = await refresh();
        if (fresh?.activeCompany?.subscription_active) {
          setTimeout(() => navigate("/app/dashboard", { replace: true }), 600);
          return;
        }
        setSyncMsg("Subscription synced. Reloading…");
        setTimeout(() => window.location.reload(), 600);
      } else {
        setSyncMsg(data?.detail || "We couldn't find an active subscription tied to your email. If you just paid, wait 30s and try again.");
        setShowSessionPaste(true);
      }
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      setShowSessionPaste(true);
    } finally {
      setSyncing(false);
    }
  };

  const syncBySession = async () => {
    const sid = sessionIdInput.trim();
    if (!sid.startsWith("cs_")) {
      setSyncMsg("That doesn't look like a Stripe session ID (should start with cs_live_… or cs_test_…).");
      return;
    }
    setSyncing(true); setSyncMsg(""); setError("");
    try {
      const { data } = await api.post("/billing/sync-session", { session_id: sid });
      if (data?.activated) {
        setSyncMsg("Confirmed with Stripe — unlocking your workspace…");
        await refresh();
        setTimeout(() => navigate("/app/dashboard", { replace: true }), 600);
      } else {
        setSyncMsg(data?.detail || "Could not activate from that session.");
      }
    } catch (e) {
      setSyncMsg(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSyncing(false);
    }
  };

  const handleLogout = async () => { await logout(); navigate("/login"); };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "var(--parapet-bg)",
        color: "#111",
        display: "flex",
        flexDirection: "column",
      }}
      data-testid="subscribe-page"
    >
      {/* Top bar */}
      <header
        style={{
          padding: "20px 40px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid var(--parapet-grid)",
        }}
      >
        <Logo />
        <div className="flex items-center gap-3 text-[12px]" style={{ color: "var(--parapet-text-secondary)" }}>
          <span>{user?.email}</span>
          <button onClick={handleLogout} className="flex items-center gap-1 underline" data-testid="subscribe-logout">
            <LogOut size={12} /> Sign out
          </button>
        </div>
      </header>

      <main
        style={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "48px 24px",
        }}
      >
        <div style={{ maxWidth: 980, width: "100%" }}>
          <div className="label-meta" style={{ marginBottom: 12 }}>SUBSCRIBE</div>
          <h1 className="display" style={{ fontSize: 36, lineHeight: 1.1, marginBottom: 10 }}>
            Activate your Parapet workspace
          </h1>
          <p className="text-[13px] mb-8" style={{ color: "var(--parapet-text-secondary)" }}>
            One plan. £50 per month. Cancel anytime.
            {activeCompany?.name && <> Workspace: <span className="value-mono">{activeCompany.name}</span>.</>}
          </p>

          {error && (
            <div className="text-[12px] p-2 border mb-4" style={{ borderColor: "#111" }} data-testid="subscribe-error">
              {error}
            </div>
          )}

          {paymentStatus && (
            <div
              className="p-3 border mb-4 flex items-center gap-2 text-[13px]"
              style={{
                borderColor: paymentStatus === "paid" ? "#0a6b2a" : paymentStatus === "timeout" || paymentStatus === "expired" ? "#7a0606" : "#111",
                background: paymentStatus === "paid" ? "#E7F5EB" : paymentStatus === "timeout" || paymentStatus === "expired" ? "#FDECEC" : "#F4F4F4",
              }}
              data-testid="subscribe-banner"
            >
              {paymentStatus === "processing" && <><span className="spinner" /> Confirming payment with Stripe…</>}
              {paymentStatus === "paid" && <>Payment confirmed — unlocking your workspace…</>}
              {paymentStatus === "expired" && <>Checkout session expired. Click subscribe to try again.</>}
              {paymentStatus === "timeout" && (
                <span className="flex items-center gap-3">
                  Still confirming with Stripe. Your payment may already be active.
                  <button
                    type="button"
                    onClick={async () => {
                      const fresh = await refresh();
                      if (fresh?.activeCompany?.subscription_active) {
                        navigate("/app/dashboard", { replace: true });
                      } else {
                        setPaymentStatus("processing");
                      }
                    }}
                    className="underline"
                    data-testid="subscribe-recheck"
                  >
                    Check again
                  </button>
                </span>
              )}
            </div>
          )}

          <div className="grid gap-6" style={{ gridTemplateColumns: "1.2fr 1fr" }} data-testid="subscribe-layout">
            {/* LEFT: What you get */}
            <div data-testid="subscribe-features">
              <div className="label-meta mb-3">WHAT YOU UNLOCK</div>
              <div className="grid gap-3" style={{ gridTemplateColumns: "1fr 1fr" }}>
                {FEATURES.map((f) => (
                  <FeatureTile key={f.title} icon={f.icon} title={f.title} blurb={f.blurb} />
                ))}
              </div>
              <div className="text-[11px] mt-6" style={{ color: "var(--parapet-text-secondary)" }}>
                All data sourced from public UK / EU / US procurement portals. No fabricated data — ever.
              </div>
            </div>

            {/* RIGHT: Pricing card */}
            {plan && (
              <div
                className="p-card"
                style={{ background: "#111", color: "#fff", padding: 24, alignSelf: "start" }}
                data-testid="subscribe-plan-card"
              >
                <div className="label-meta" style={{ color: "#999" }}>{plan.name}</div>
                <div className="display" style={{ fontSize: 44, marginTop: 4 }}>
                  £{plan.price_gbp.toFixed(0)}
                  <span className="text-[13px] font-normal" style={{ color: "#999" }}>/month</span>
                </div>
                <div className="text-[13px] mt-4" style={{ color: "#ccc", lineHeight: 1.55 }}>
                  Full access to every Parapet workspace, source feed and analysis tool. No tiers, no add-ons.
                </div>
                <button
                  onClick={startCheckout}
                  disabled={loading || paymentStatus === "processing"}
                  className="w-full mt-6"
                  style={{
                    background: "#fff",
                    color: "#111",
                    border: 0,
                    padding: "12px 16px",
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: "pointer",
                    borderRadius: 4,
                  }}
                  data-testid="subscribe-cta"
                >
                  {loading ? "Redirecting to Stripe…" : "Subscribe with Stripe"}
                </button>
                <div className="text-[11px] mt-3" style={{ color: "#999" }}>
                  Secure payment via Stripe · Card details never touch our servers · Cancel anytime
                </div>
                <div
                  className="mt-4 pt-4"
                  style={{ borderTop: "1px solid #333", textAlign: "center" }}
                >
                  <div className="text-[12px]" style={{ color: "#ccc" }}>
                    Already paid?{" "}
                    <button
                      type="button"
                      onClick={syncSubscription}
                      disabled={syncing}
                      className="underline"
                      style={{
                        background: "transparent",
                        border: 0,
                        color: "#fff",
                        cursor: syncing ? "wait" : "pointer",
                        padding: 0,
                        font: "inherit",
                      }}
                      data-testid="subscribe-sync"
                    >
                      {syncing ? "Checking with Stripe…" : "Refresh my subscription"}
                    </button>
                  </div>
                  {syncMsg && (
                    <div className="text-[11px] mt-2" style={{ color: "#9bd9a9" }} data-testid="subscribe-sync-msg">
                      {syncMsg}
                    </div>
                  )}
                  {showSessionPaste && (
                    <div className="mt-3 text-left">
                      <div className="text-[11px] mb-2" style={{ color: "#bbb" }}>
                        Still stuck? Find your Checkout Session ID in your Stripe email receipt (starts with <span style={{fontFamily:"ui-monospace,SFMono-Regular,Menlo,monospace", color:"#fff"}}>cs_</span>), paste below:
                      </div>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={sessionIdInput}
                          onChange={(e) => setSessionIdInput(e.target.value)}
                          placeholder="cs_live_..."
                          className="flex-1 p-input"
                          style={{ fontSize: 12, padding: "6px 8px", background: "#222", color: "#fff", borderColor: "#555" }}
                          data-testid="subscribe-session-input"
                        />
                        <button
                          type="button"
                          onClick={syncBySession}
                          disabled={syncing || !sessionIdInput.trim()}
                          style={{
                            background: "#fff", color: "#111", border: 0,
                            padding: "6px 12px", fontSize: 12, fontWeight: 600,
                            cursor: syncing || !sessionIdInput.trim() ? "not-allowed" : "pointer",
                            borderRadius: 4, opacity: syncing || !sessionIdInput.trim() ? 0.5 : 1,
                          }}
                          data-testid="subscribe-session-submit"
                        >
                          Activate
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      <footer
        style={{
          padding: "20px 40px",
          borderTop: "1px solid var(--parapet-grid)",
          fontSize: 12,
          color: "var(--parapet-text-secondary)",
          textAlign: "center",
        }}
      >
        <div>
          <a href="/unstuck" className="underline" data-testid="subscribe-unstuck-link">Paid but still locked out?</a>
        </div>
        <div style={{ marginTop: 10, fontSize: 11, lineHeight: 1.5 }}>
          © {new Date().getFullYear()} Parapet. Parapet is a trading name of Linkup Ltd,
          a company registered in England and Wales under company number 17060828.
        </div>
      </footer>
    </div>
  );
}

function FeatureTile({ icon: Icon, title, blurb }) {
  return (
    <div
      style={{
        padding: 14,
        border: "1px solid var(--parapet-grid)",
        borderRadius: 4,
        background: "#fff",
      }}
    >
      <Icon size={16} style={{ color: "#111" }} />
      <div className="text-[13px] font-semibold mt-2" style={{ letterSpacing: "-0.01em" }}>
        {title}
      </div>
      <div className="text-[12px] mt-1" style={{ color: "var(--parapet-text-secondary)", lineHeight: 1.45 }}>
        {blurb}
      </div>
    </div>
  );
}
